import { mergeServiceDef } from '../../utils/frontendConfigMerge';
import { getUserTrainerId } from '../../utils/branding';
import { isFoodExternal, getFoodModulePublicUrl } from '../../utils/foodModule';
import { hasPaidFoodAccess } from '../../utils/foodAccess';
import { isTrainingExternal, getTrainingModulePublicUrl } from '../../utils/trainingModule';
import { getFutbolModulePublicUrl, isFutbolOnlyConsumer } from '../../utils/futbolModule';
import { hasActiveConsumerLicense, isConsumerAccessBlocked, isCoachClientSubscriptionActive } from '../../utils/subscriptionAccess';
import { isHoldingMarcaAdmin, isSedeFinalUser } from '../../utils/gymRoleScope';
import { isFoodOnlyDomainUser } from '../../utils/adminUserScopes';
import { canAccessFitLive, canManageFitLiveStudio, isFitLiveUiEnabled, isTrainingShellUser } from '../../utils/fitLiveModule';

// Resuelve qué SERVICIOS ve un usuario en su pantalla de inicio.
//
// Reglas (alineadas con docs/ECOSISTEMA_MODULAR_MARCA_BLANCA.md):
//   - Cada usuario hace UN registro y puede tener uno o varios servicios.
//   - Los servicios disponibles se determinan a partir de:
//       1) `user.module_access` (objeto explícito por módulo si está poblado).
//       2) En su defecto, los roles del usuario.
//   - Solo `super_admin` ve TODOS los servicios.
//   - Cada `admin_*` específico solo ve el servicio que administra.
//   - Cada servicio se renderiza con la misma identidad visual (foto + título)
//     pero su acción cambia según el rol: usuario final entra a su experiencia
//     de consumo; admin/coach entra al maestro de ese servicio.
//
// Orden visual: D28D → Gimnasio (producto/licencia, panel D28D) → Food → Training → Live
//
// IMPORTANTE: los GIMNASIOS NO son un servicio independiente. Viven bajo
// D28D porque consumen el contenido D28D y agendan sobre las plantillas de
// clases en vivo de D28D. Por eso "Mi Gimnasio" / "Marca Blanca" aparece
// como tarjeta DENTRO del panel D28D (D28DAdminView), no como card del Hero.

const SERVICE_DEFS = [
  {
    id: 'd28d',
    title: 'Programa en vivo',
    desc: 'Comunidad, ciclos y clases en vivo. Si perteneces a un gimnasio virtual, entra desde tu marca.',
    descAdmin: 'Programas de Fit Live, gimnasios marca blanca (solo plataforma crea sedes), clases en vivo y asistencia.',
    img: 'https://images.unsplash.com/photo-1518611012118-696072aa579a?auto=format&fit=crop&w=800&q=80',
    alt: 'Sesión de entrenamiento grupal',
  },
  {
    id: 'food-plan',
    title: 'FOOD_PLAN',
    desc: 'Tu alimentación guiada por tu equipo.',
    descAdmin: 'Calculadora, alimentos, equivalentes, recetas y registro.',
    img: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&w=800&q=80',
    alt: 'Plato saludable',
  },
  {
    id: 'training',
    title: 'Entrenadores',
    desc: 'Tu rutina del día y seguimiento con tu coach.',
    descAdmin: 'Rutinas, galería y usuarios asignados.',
    img: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?auto=format&fit=crop&w=800&q=80',
    alt: 'Entrenamiento personalizado',
  },
  {
    id: 'futbol',
    title: 'futbol.app',
    desc: 'Tu escuela, calendario, desarrollo y academia virtual.',
    descAdmin: 'Escuelas, equipos, jugadores, metodología y seguimiento.',
    img: 'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?auto=format&fit=crop&w=800&q=80',
    alt: 'Entrenamiento de fútbol formativo',
  },
  {
    id: 'gym',
    title: 'Mi Gimnasio',
    desc: 'Clases en vivo de Fit Live · Programa, usuarios de tu sede y marca blanca.',
    descAdmin: 'Operación del gimnasio afiliado dentro de Fit Live · Programa (no es módulo externo).',
    img: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=800&q=80',
    alt: 'Gimnasio marca blanca',
    panelTarget: 'd28d',
  },
  {
    id: 'live-classes',
    title: 'Clases en Vivo',
    desc: 'Calendario de clases en vivo: filtra, inscríbete y entra al Zoom.',
    descAdmin: 'Calendario de clases en vivo: consulta, filtra e inscripción.',
    img: 'https://images.unsplash.com/photo-1549060279-7e168fcee0c2?auto=format&fit=crop&w=800&q=80',
    alt: 'Clase en vivo de fitness',
  },
  {
    id: 'fit-live',
    title: 'Fit Live',
    desc: 'Marketplace de clases en vivo, tokens y creators.',
    descAdmin: 'Creator Studio · Control Tower · marketplace Fit Live (módulo nuevo).',
    img: 'https://images.unsplash.com/photo-1518611012118-696072aa579a?auto=format&fit=crop&w=800&q=80',
    alt: 'Fit Live marketplace',
  },
];

const ADMIN_ROLES = new Set([
  'super_admin', 'admin_marca', 'admin_gym_virtual', 'admin_gimnasio', 'admin_gym', 'admin_sede',
  'admin_d28d', 'admin_food_plan', 'admin_food',
  'admin_training', 'admin_entrenador',
  'entrenador', 'entrenador_d28d', 'entrenador_gym', 'nutricionista',
]);

export function getRolesArr(user) {
  const primary = user?.rol || 'usuario_final';
  const extra = Array.isArray(user?.roles) ? user.roles : [];
  return [...new Set([primary, ...extra].filter(Boolean))];
}

function isAdminish(user) {
  return getRolesArr(user).some((r) => ADMIN_ROLES.has(r));
}

/** Usuario consumidor (no admin/coach). */
export function isFinalConsumer(user) {
  return !isAdminish(user);
}

/** Usuario final con plan comercial FOOD (sin coach): solo producto Food.
 *  Ignora gym_id contaminado (ancla D28D) si plan/licencias son Food-only (paridad isFoodOnlyDomainUser). */
export function isFoodOnlyCommercialUser(user) {
  if (!user || !isFinalConsumer(user)) return false;
  if (getUserTrainerId(user)) return false;
  const host = String(user.entry_host || '').toLowerCase();
  if (host === 'training' || host === 'd28d') return false;
  if (host === 'food') return true;

  const licenses = Array.isArray(user?.licenses) ? user.licenses : [];
  const active = licenses.filter((lic) => lic.active !== false);
  const codes = new Set(active.map((lic) => String(lic.module_code || '').toLowerCase()));
  const kind = String(
    user?.subscription?.kind || user?.subscription?.plan_kind || user?.plan_kind || '',
  ).toLowerCase();
  const plan = String(user?.subscription?.plan || user?.planId || user?.plan || '').trim().toUpperCase();
  const foodNamed = kind === 'food' || plan === 'FOOD_PLAN' || plan.startsWith('FOOD');
  // Plan FOOD gana a licencias D28D fantasma; Training pagado sí es otro producto.
  if (foodNamed && !codes.has('training')) return true;

  const hasOtherProduct = ['d28d', 'gym', 'live_classes', 'training', 'futbol'].some((c) => codes.has(c));
  if (hasOtherProduct) return false;

  if (isFoodOnlyDomainUser(user)) return true;
  if (active.length > 0) {
    const onlyFood = [...codes].every((c) => c === 'food' || c === 'food_plan');
    if (onlyFood) return true;
  }
  return false;
}

function accessFromActiveLicenses(user) {
  const access = {};
  const licenses = Array.isArray(user?.licenses) ? user.licenses : [];
  for (const lic of licenses) {
    if (lic.active === false) continue;
    switch (lic.module_code) {
      case 'food':
        access.food_plan = true;
        access.nutrition = true;
        break;
      case 'training':
        access.training = true;
        break;
      case 'futbol':
        access.futbol = true;
        break;
      case 'd28d':
        access.d28d = true;
        break;
      case 'gym':
        access.gym = true;
        access.d28d = true;
        break;
      case 'live_classes':
        access.live_classes = true;
        break;
      default:
        break;
    }
  }
  return access;
}

function mapAccessToServiceIds(access, roles = [], user = null) {
  const mapped = [];
  if (!roles.includes('entrenador') && (access.gym || access.d28d)) mapped.push('d28d');
  if (isCoachClientUser(user)) {
    if (!isCoachClientSubscriptionActive(user)) return mapped;
    if (!isConsumerAccessBlocked(user)) {
      if (hasPaidFoodAccess(user)) mapped.push('food-plan');
      mapped.push('training');
    }
  } else if ((access.food_plan || access.nutrition) && !hideFoodForGymVirtualConsumer(user)) {
    mapped.push('food-plan');
  }
  if (access.training && !(user?.gym_id || user?.gymId) && !roles.includes('entrenador')) {
    mapped.push('training');
  } else if (access.training && roles.includes('entrenador')) {
    mapped.push('training');
  }
  if (access.futbol) mapped.push('futbol');
  if (!roles.includes('entrenador') && access.live_classes) mapped.push('live-classes');
  if (!mapped.length) return [];
  return orderServiceIds([...new Set(mapped)]);
}

/** Dual-read: licencias activas primero; legacy solo si no hay filas de licencia. */
export function getEffectiveModuleAccess(user) {
  const legacy = user?.module_access && typeof user.module_access === 'object'
    ? { ...user.module_access }
    : {};
  const licenses = Array.isArray(user?.licenses) ? user.licenses : [];
  const activeRows = licenses.filter((lic) => lic.active !== false);

  if (activeRows.length) {
    const fromLicenses = accessFromActiveLicenses(user);
    if (typeof legacy.d28d_program === 'string' && legacy.d28d_program.trim()) {
      fromLicenses.d28d_program = legacy.d28d_program.trim();
    }
    if (typeof legacy.futbol_role === 'string' && legacy.futbol_role.trim()) {
      fromLicenses.futbol_role = legacy.futbol_role.trim();
    }
    return fromLicenses;
  }

  return normalizeConsumerModuleAccess(legacy);
}

function normalizeConsumerModuleAccess(raw = {}) {
  const access = {};
  for (const [k, v] of Object.entries(raw || {})) {
    if (v === null || v === undefined || v === false || v === '') continue;
    if (k === 'd28d_program' || k === 'futbol_role') {
      if (typeof v === 'string' && v.trim()) access[k] = v.trim();
      continue;
    }
    access[k] = true;
  }
  return access;
}

// Devuelve los IDs de servicios habilitados para el usuario dado.
//
// REGLA ESTRICTA POR ROL:
//   - super_admin → TODOS los servicios (4).
//   - admin_d28d → solo d28d + live-classes (las clases en vivo viven en D28D).
//   - admin_food / admin_food_plan → solo food-plan.
//   - admin_entrenador / admin_training → solo training.
//   - admin_gym / admin_marca / admin_gimnasio → entran por D28D (su gimnasio
//     consume contenido D28D y agenda en plantillas D28D). Si su gym contrata
//     food-plan o entrenadores, también ven esos.
//   - entrenador (coach individual) → training + food-plan + live-classes (consumo).
//   - nutricionista → food-plan.
//   - usuario_final → solo licencias activas / module_access (nunca todos los servicios por defecto).
export function getEnabledServiceIds(user) {
  if (!user) return [];
  if (isHoldingMarcaAdmin(user)) return [];
  // Food-only / futbol-only pendiente: conservar tile para boot/routing.
  if (isFinalConsumer(user) && isConsumerAccessBlocked(user)) {
    if (isFutbolOnlyConsumer(user)) return ['futbol'];
    return isFoodOnlyCommercialUser(user) ? ['food-plan'] : [];
  }
  const roles = getRolesArr(user);
  const access = getEffectiveModuleAccess(user);
  const hasAccessKeys = Object.keys(access).some((k) => access[k]);

  // 1) super_admin: con Fit Live activo no se expone marca/módulo legacy en servicios.
  if (roles.includes('super_admin')) {
    if (isFitLiveUiEnabled() && canManageFitLiveStudio(user)) {
      return ['fit-live', 'food-plan', 'training', 'futbol'];
    }
    return ['d28d', 'food-plan', 'training', 'futbol', 'live-classes'];
  }

  // 2) Admin específico: solo SU servicio (override estricto sobre module_access).
  // admin_d28d / admin_sede / gym ops: nunca Food (productos independientes).
  const isFoodAdmin = roles.includes('admin_food') || roles.includes('admin_food_plan');
  if (roles.includes('admin_d28d') && !roles.includes('super_admin') && !isFoodAdmin) {
    return ['d28d', 'live-classes'];
  }
  if (
    (roles.includes('admin_sede')
      || roles.includes('admin_gym_virtual')
      || roles.includes('admin_gimnasio')
      || roles.includes('admin_gym'))
    && !roles.includes('super_admin')
    && !isFoodAdmin
    && !isHoldingMarcaAdmin(user)
  ) {
    return ['d28d'];
  }
  if (isFoodAdmin && roles.length === 1) {
    return ['food-plan'];
  }
  if ((roles.includes('admin_entrenador') || roles.includes('admin_training')) && roles.length === 1) {
    return ['training'];
  }
  if ((roles.includes('entrenador_d28d') || roles.includes('entrenador_gym')) && roles.length === 1) {
    return ['live-classes'];
  }
  if (roles.includes('entrenador') && roles.length === 1) {
    // Training sigue siendo el hogar; Fit Live solo si es creator (Studio), sin mezclar listados.
    return canManageFitLiveStudio(user) ? ['training', 'fit-live'] : ['training'];
  }

  // 3) Usuario final: licencias activas; sede con sede activa hereda D28D aunque el JWT aún no tenga licencias.
  if (isFinalConsumer(user)) {
    // Fit Live on: producto nuevo — sin D28D/live-classes ni gate de galería legacy.
    if (isFitLiveUiEnabled()) {
      // futbol.app es producto independiente: el jugador/coach solo-fútbol no cae en Fit Live.
      if (isFutbolOnlyConsumer(user)) return ['futbol'];
      if (isFoodOnlyCommercialUser(user)) {
        const foodOk = hasPaidFoodAccess(user)
          || access.food_plan
          || access.nutrition
          || accessFromActiveLicenses(user).food_plan;
        return foodOk ? ['food-plan'] : [];
      }
      if (isTrainingShellUser(user)) {
        const ids = [];
        if (access.futbol || accessFromActiveLicenses(user).futbol) ids.push('futbol');
        const foodOk = hasPaidFoodAccess(user)
          || access.food_plan
          || access.nutrition
          || accessFromActiveLicenses(user).food_plan;
        if (foodOk && !hideFoodForGymVirtualConsumer(user)) ids.push('food-plan');
        const coachClient = isCoachClientUser(user);
        if (
          (coachClient && isCoachClientSubscriptionActive(user) && (access.training || accessFromActiveLicenses(user).training))
          || access.training
          || accessFromActiveLicenses(user).training
        ) {
          ids.push('training');
        }
        return ids.length ? ids : ['training'];
      }
      const ids = ['fit-live'];
      if (access.futbol || accessFromActiveLicenses(user).futbol) ids.push('futbol');
      const foodOk = hasPaidFoodAccess(user)
        || access.food_plan
        || access.nutrition
        || accessFromActiveLicenses(user).food_plan;
      if (foodOk && !hideFoodForGymVirtualConsumer(user)) ids.push('food-plan');
      const coachClient = isCoachClientUser(user);
      if (
        coachClient
        && isCoachClientSubscriptionActive(user)
        && (access.training || accessFromActiveLicenses(user).training)
      ) {
        ids.push('training');
      }
      return ids;
    }
    // Plan FOOD_PLAN self-serve: no mostrar D28D/Training aunque module_access esté contaminado.
    if (isFoodOnlyCommercialUser(user)) {
      const foodOk = hasPaidFoodAccess(user)
        || access.food_plan
        || access.nutrition
        || accessFromActiveLicenses(user).food_plan;
      return foodOk ? ['food-plan'] : [];
    }
    const licensed = hasActiveConsumerLicense(user);
    if (!licensed) {
      const sedeMember = isSedeFinalUser(user);
      if (sedeMember && user?.sede_branch_active === true) {
        const syntheticAccess = {
          d28d: true,
          gym: true,
          live_classes: true,
          d28d_program: access.d28d_program,
        };
        return orderServiceIds(mapAccessToServiceIds(syntheticAccess, roles, user));
      }
      return [];
    }
    const ids = mapAccessToServiceIds(accessFromActiveLicenses(user), roles, user);
    const coachClient = isCoachClientUser(user);
    const withoutTraining = roles.some((r) => ['entrenador', 'nutricionista'].includes(r))
      ? ids
      : ids.filter((id) => id !== 'training');
    const withCoachTraining = coachClient
      && isCoachClientSubscriptionActive(user)
      && !withoutTraining.includes('training')
      ? [...withoutTraining, 'training']
      : withoutTraining;
    // Gym virtual / sede: Food solo con licencia/compra o food_plan del maestro.
    const withoutFood = hideFoodForGymVirtualConsumer(user)
      ? withCoachTraining.filter((id) => id !== 'food-plan')
      : withCoachTraining;
    return orderServiceIds([...new Set(withoutFood)]);
  }

  // 4) Licencias + module_access (dual-read) — admins / coaches con acceso mixto.
  if (hasAccessKeys && !isHoldingMarcaAdmin(user)) {
    const mapped = [];
    if (!roles.includes('entrenador') && (access.gym || access.d28d)) mapped.push('d28d');
    if (access.food_plan || access.nutrition) mapped.push('food-plan');
    if (access.training) mapped.push('training');
    if (access.futbol) mapped.push('futbol');
    if (!roles.includes('entrenador') && access.live_classes) mapped.push('live-classes');
    if (mapped.length > 0) return orderServiceIds([...new Set(mapped)]);
  }

  // 5) Resolución por combinaciones de rol.
  const ids = new Set();
  if (roles.includes('admin_d28d')) {
    ids.add('d28d');
    ids.add('live-classes');
  }
  if (roles.includes('admin_food') || roles.includes('admin_food_plan')) {
    ids.add('food-plan');
  }
  if (roles.includes('admin_entrenador') || roles.includes('admin_training')) {
    ids.add('training');
  }
  // Gimnasios marca blanca operativos (no holding).
  if ((roles.includes('admin_gym_virtual') || roles.includes('admin_gimnasio') || roles.includes('admin_gym'))
    && !isHoldingMarcaAdmin(user)) {
    ids.add('d28d');
  }
  if (roles.includes('entrenador')) {
    ids.add('training');
  }
  if (roles.includes('nutricionista')) {
    ids.add('food-plan');
  }
  if (ids.size > 0) return orderServiceIds(Array.from(ids));

  return [];
}

function orderServiceIds(ids) {
  const normalized = ids.filter((id) => id !== 'gym');
  // D28D + live-classes es un solo producto para el usuario final.
  const set = new Set(normalized);
  if (set.has('d28d') && set.has('live-classes')) {
    set.delete('live-classes');
  }
  const order = ['fit-live', 'd28d', 'food-plan', 'training', 'futbol', 'live-classes'];
  return order.filter((id) => set.has(id));
}

/** Usuario final con solo entrenamiento (sin D28D ni Food). */
export function isTrainingOnlyFinalUser(services) {
  if (!services?.length) return false;
  const ids = new Set(services.map((s) => s.id));
  return ids.has('training') && !ids.has('d28d') && !ids.has('live-classes') && !ids.has('food-plan') && !ids.has('futbol');
}

/** Clientes de coach: solo pueden contratar FOOD PLAN como addon en Mi cuenta. */
export function coachClientAllowedAddonIds(user) {
  return isCoachClientUser(user) ? ['food'] : null;
}

/** Usuario final bajo coach (marca blanca posible). */
export function isCoachClientUser(user) {
  return Boolean(getUserTrainerId(user));
}

/** Usuario final con solo programa D28D (sin Food ni Training). */
export function isD28dOnlyFinalUser(services) {
  if (!services?.length) return false;
  const ids = new Set(services.map((s) => s.id));
  const hasD28d = ids.has('d28d') || ids.has('live-classes');
  const hasOther = ids.has('food-plan') || ids.has('training') || ids.has('futbol');
  return hasD28d && !hasOther;
}

export function getD28dProgramId(user) {
  const raw = user?.module_access?.d28d_program || user?.program_id || user?.programa_d28d;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
}

/** Usuario final con gym_id de marca blanca / gym virtual (no ancla plataforma id=1). */
export function isVirtualGymMember(user) {
  const gid = user?.gym_id ?? user?.gymId ?? null;
  if (gid == null || gid === '') return false;
  return Number(gid) !== 1;
}

/**
 * Consumidores de gimnasio virtual / sede: no Food en shell por defecto.
 * Sí ven Food si lo compraron (licencia) o el super_admin les habilitó food_plan en el maestro.
 */
export function hideFoodForGymVirtualConsumer(user, gym = null) {
  if (!user) return false;
  if (isFoodOnlyCommercialUser(user)) return false;
  if (hasPaidFoodAccess(user)) return false;
  const access = user?.module_access && typeof user.module_access === 'object'
    ? user.module_access
    : {};
  if (access.food_plan === true || access.nutrition === true) return false;
  if (isSedeFinalUser(user, gym)) return true;
  return isVirtualGymMember(user);
}

export function getServicesFor(user, frontendConfig = null, lang = 'es') {
  const ids = new Set(getEnabledServiceIds(user));
  const adminMode = isAdminish(user);

  return SERVICE_DEFS
    .filter((s) => ids.has(s.id) && s.id !== 'gym')
    .map((s) => {
      const merged = mergeServiceDef(s, frontendConfig, s.id, adminMode, lang);
      const panelTarget = s.panelTarget || s.id;
      const adminDestination = panelTarget === 'd28d' && s.id === 'gym'
        ? 'service:d28d'
        : `service:${s.id}`;
      const externalUrl = s.id === 'food-plan' && isFoodExternal()
        ? getFoodModulePublicUrl()
        : s.id === 'training' && isTrainingExternal()
          ? getTrainingModulePublicUrl()
          : s.id === 'futbol'
            ? getFutbolModulePublicUrl()
            : null;
      let destinationView = adminMode
        ? adminDestination
        : userFacingDestinationFor(s.id);
      if (s.id === 'live-classes') destinationView = 'liveclasses';
      if (externalUrl && s.id === 'food-plan' && !adminMode) destinationView = 'external:food';
      if (s.id === 'training' && isTrainingExternal()) destinationView = 'external:training';
      if (s.id === 'futbol') destinationView = 'external:futbol';
      return {
        ...merged,
        externalUrl,
        destinationView,
        moduleLaunch: s.id === 'training'
          ? 'training'
          : s.id === 'food-plan'
            ? 'food'
            : s.id === 'futbol'
              ? 'futbol'
              : null,
      };
    });
}

export { SERVICE_DEFS };

function userFacingDestinationFor(serviceId) {
  switch (serviceId) {
    case 'food-plan': return isFoodExternal() ? 'external:food' : 'myplan';
    case 'training': return isTrainingExternal() ? 'external:training' : 'training';
    case 'futbol': return 'external:futbol';
    case 'fit-live': return 'service:fit-live';
    case 'd28d':
    case 'live-classes':
      return 'liveclasses';
    default: return 'myplan';
  }
}

export function isAdminUser(user) {
  return isAdminish(user);
}
